import { Editor } from "codemirror";
import dynamic from "next/dynamic";
import React from "react";
import { useTranslation } from "react-i18next";

const CodeMirrorLoading = () => {
    const [t] = useTranslation();
    return (
        <div className="font-mono flex min-h-32 w-full items-center justify-center border-0 bg-ant-primary-hover p-4 text-ant-sm">
            {t("inputs.RichText.CodeMirrorLoading")}
        </div>
    );
};

export const DynamicCodeMirror = dynamic(
    async () => {
        await import("./codemirror-setup");

        const { Controlled } = await import("react-codemirror2");
        return Controlled;
    },
    {
        ssr: false,
        loading: CodeMirrorLoading,
    },
);

// Language to CodeMirror mode mapping
export const getCodeMirrorMode = (language: string): string => {
    switch (language.toLowerCase()) {
        case "javascript":
        case "js":
            return "javascript";
        case "typescript":
        case "ts":
            return "text/typescript";
        case "python":
        case "py":
            return "python";
        case "sql":
            return "text/x-sql";
        case "java":
            return "text/x-java";
        case "css":
            return "css";
        case "html":
            return "htmlmixed";
        case "xml":
            return "xml";
        case "markdown":
        case "md":
            return "markdown";
        case "shell":
        case "bash":
        case "sh":
            return "shell";
        case "yaml":
        case "yml":
            return "yaml";
        case "php":
            return "php";
        case "ruby":
        case "rb":
            return "ruby";
        default:
            return "text/plain";
    }
};

interface CodeMirrorBaseProps {
    value: string;
    language: string;
    readOnly?: boolean;
    onBeforeChange?: (editor: Editor, data: any, value: string) => void;
    onBlur?: () => void;
    children?: React.ReactNode;
}

export const CodeMirrorBase: React.FC<CodeMirrorBaseProps> = ({
    value,
    language,
    readOnly = true,
    onBeforeChange,
    onBlur,
    children,
}) => {
    return (
        <div className="my-4 overflow-hidden rounded-lg border">
            <div
                className="[&_.CodeMirror]:h-full! [&_.CodeMirror]:min-h-24! [&_.CodeMirror]:p-4! [&_.CodeMirror]:pb-0! [&_.CodeMirror]:text-ant-sm [&_.CodeMirror-scroll]:min-h-24!"
                contentEditable={false}
                suppressContentEditableWarning={true}
            >
                <DynamicCodeMirror
                    value={value}
                    options={{
                        mode: getCodeMirrorMode(language),
                        lineNumbers: false,
                        lineWrapping: true,
                        readOnly: readOnly,
                    }}
                    className="min-h-24"
                    onBeforeChange={onBeforeChange ? onBeforeChange : () => {}}
                    onBlur={onBlur ? onBlur : () => {}}
                />

                {children}
            </div>
        </div>
    );
};

export default CodeMirrorBase;
